import moment from 'moment';
import { Bookmark } from '../../me/bookmarks/types';
import {
  Recording,
  SeriesRecording,
  SeriesRecordingStatus,
  EpisodeRecording,
  MovieRecording,
  SingleRecording,
  SingleEpisodeRecording,
  BaseEpisodeRecording,
  LinkedEpisodeRecording,
  SingleRecordingStatus,
} from '../types/recording';
import {
  BaseTask,
  EpisodeTask,
  HuaweiRecording,
  MovieTask,
  ParentTask,
  RecordingTask,
} from '../types/huawei-recording';
import { ExtensionInfo } from './extension-info';
import { Cast } from '../../titles/types/shared';

export function createRecordings(recordings: HuaweiRecording[], bookmarks: Bookmark[]): Recording[] {
  return recordings.map((recording) => {
    if ('pvrList' in recording) {
      return createSeriesRecording(recording, bookmarks);
    } else {
      return createSingleRecording(recording, bookmarks);
    }
  });
}

function createSeriesRecording(recording: ParentTask, bookmarks: Bookmark[]): SeriesRecording {
  const episodes = recording.pvrList.map((rec) => createEpisodeRecording(rec, bookmarks));
  const sortedEpisodes = episodes.sort(
    (a, b) => b.season.sequenceNumber - a.season.sequenceNumber || a.episode.sequenceNumber - b.episode.sequenceNumber,
  );

  return {
    type: 'series',
    periodicTaskId: recording.periodPVRTaskId,
    name: recording.periodPVRTaskName,
    seriesId: recording.seriesId,
    channelId: recording.channelId,
    channelName: recording.channelName,
    deleteMode: recording.deleteMode === '0' ? 'auto' : 'manual',
    status: seriesStatus(recording),
    image: createRecordingImage(recording),
    startTime: moment(recording.effectivetime).format(),
    latestEpisode: createEpisodeRecording(recording.latestPVRTask, bookmarks),
    episodes: sortedEpisodes,
  };
}

function createSingleRecording(recording: RecordingTask, bookmarks: Bookmark[]): SingleRecording {
  if (recording.subNum !== undefined) {
    return createEpisodeRecording(recording, bookmarks);
  }

  return createSingleMovieRecording(recording, bookmarks);
}

function createSingleMovieRecording(recording: MovieTask, bookmarks: Bookmark[]): MovieRecording {
  const start = moment(recording.beginTime);
  const end = moment(recording.endTime);
  const status = singleStatus(recording);
  const deleteMode = recording.deleteMode === '0' ? 'auto' : 'manual';
  const bookmark = getRelatedBookmark(bookmarks, recording);

  const info = new ExtensionInfo(recording);
  const originalName = info.get('ORIGINAL_TITLE');
  const ageRating = info.get('RATING_NO') ?? '';
  const description = recording.introduce ?? '';

  return {
    type: 'single',
    id: recording.pvrId,
    name: recording.pvrName ?? originalName,
    channelId: recording.channelId,
    channelName: recording.channelName,
    programId: recording.programId,
    lifetimeId: recording.lifetimeId,
    mediaId: recording.mediaId,
    startTime: start.format(),
    endTime: end.format(),
    startOffset: parseInt(recording.beginOffset, 10),
    endOffset: parseInt(recording.endOffset, 10),
    availableFrom: start.format(),
    availableTo: moment(start).add(999, 'y').format(),
    image: createRecordingImage(recording),
    genres: recording.genreIds,
    country: recording.country,
    duration: parseInt(recording.realRecordLength, 10),
    cast: recording.casts ? createCast(recording.casts) : undefined,
    originalName: originalName ?? recording.pvrName,
    description,
    ageRating,
    deleteMode,
    bookmark,
    status,
  };
}

function createEpisodeRecording(episode: EpisodeTask, bookmarks: Bookmark[]): EpisodeRecording {
  if (isSeriesLinked(episode)) {
    return createLinkedEpisodeRecording(episode, bookmarks);
  }

  return createSingleEpisodeRecording(episode, bookmarks);
}

function createBaseEpisodeRecording<T extends BaseEpisodeRecording['type']>(
  type: T,
  episode: EpisodeTask,
  bookmarks: Bookmark[],
): BaseEpisodeRecording & { type: T } {
  const start = moment(episode.beginTime);
  const end = moment(episode.endTime);
  const deleteMode = episode.deleteMode === '0' ? 'auto' : 'manual';
  const bookmark = getRelatedBookmark(bookmarks, episode);

  const info = new ExtensionInfo(episode);
  const originalName = info.get('ORIGINAL_TITLE') || episode.pvrName;
  const ageRating = info.get('RATING_NO') ?? '';
  const description = episode.introduce ?? '';

  return {
    type: type,
    id: episode.pvrId,
    periodicTaskId: episode.periodPVRTaskId,
    name: episode.subName || episode.pvrName || originalName,
    channelId: episode.channelId,
    channelName: episode.channelName,
    programId: episode.programId,
    mediaId: episode.mediaId,
    lifetimeId: episode.lifetimeId,
    startTime: start.format(),
    endTime: end.format(),
    startOffset: parseInt(episode.beginOffset, 10),
    endOffset: parseInt(episode.endOffset, 10),
    availableFrom: start.format(),
    availableTo: moment(start).add(999, 'y').format(),
    image: createRecordingImage(episode),
    genres: episode.genreIds,
    country: episode.country,
    duration: parseInt(episode.realRecordLength, 10),
    series: {
      id: episode.seriesId,
      name: episode.pvrName,
    },
    season: {
      sequenceNumber: parseInt(episode.seasonNum, 10),
    },
    episode: {
      sequenceNumber: parseInt(episode.subNum, 10),
      name: episode.subName,
    },
    status: singleStatus(episode),
    cast: episode.casts ? createCast(episode.casts) : undefined,
    originalName,
    description,
    ageRating,
    deleteMode,
    bookmark,
  };
}

function createSingleEpisodeRecording(episode: EpisodeTask, bookmarks: Bookmark[]): SingleEpisodeRecording {
  return {
    ...createBaseEpisodeRecording('single', episode, bookmarks),
    periodicTaskId: undefined,
  };
}

function createLinkedEpisodeRecording(episode: EpisodeTask, bookmarks: Bookmark[]): LinkedEpisodeRecording {
  if (!episode.periodPVRTaskId) {
    throw new Error('Missing periodPVRTaskId');
  }
  return {
    ...createBaseEpisodeRecording('linked', episode, bookmarks),
    periodicTaskId: episode.periodPVRTaskId,
    isSeriesLinked: isSeriesLinked(episode),
  };
}

function isSeriesLinked(episode: EpisodeTask) {
  if (!episode.periodPVRTaskId) {
    return false;
  }

  // The series has been unscheduled
  if (episode.overtime) {
    return false;
  }

  return true;
}

function seriesStatus(recording: ParentTask): SeriesRecordingStatus {
  if (!recording.overtime || recording.overtime === '-1') {
    return 'scheduled';
  }

  return 'cancelled';
}

function singleStatus(recording: RecordingTask): SingleRecordingStatus {
  switch (recording.status) {
    case '-3':
      return 'scheduled';
    case '-2':
      return 'ongoing';
    case '-1':
      return 'scheduled';
    case '0':
      return 'completed';
    case '1':
      return 'ongoing';
    case '2':
      return 'failed';
    case '8':
      return 'partial';
    default:
      return 'failed';
  }
}

function createRecordingImage(recording: HuaweiRecording): string {
  if ('latestPVRTask' in recording) {
    return recording.latestPVRTask.pictures?.[0]?.href ?? '';
  }

  return recording.pictures?.[0]?.href ?? '';
}

const ROLE_MAP = {
  0: 'CastMember',
  1: 'Director',
  4: 'Producer',
  5: 'Writer',
} as const;

function createCast(cast: NonNullable<BaseTask['casts']>): Cast[] {
  return cast.map((person) => ({
    name: person.castName,
    role: ROLE_MAP[person.roleType],
  }));
}

function getRelatedBookmark(bookmarks: Bookmark[], recording: RecordingTask) {
  return bookmarks.find((b) => b.platformTitleId === recording.programId || b.platformTitleId === recording.pvrId);
}
